import React, { useMemo, useState, useCallback } from 'react'
import { Bar, BarChart as RCBarChart, Brush, ReferenceLine, ResponsiveContainer, ReferenceArea } from 'recharts'
import { CartesianGrid, XAxis, YAxisLeft, YAxisRight } from '../../shared/utils/rechartComps'
import { traceConf } from '../../../system-charts/shared/config/traceConf'
import { getUniqueIdentifierFromCustomTrace } from '../../../system-charts/shared/utils/getUniqueIdentifierFromCustomTrace'
import { getChartColor } from 'src/Libs/system-charts/shared/config/chartColors'
import { useTheme } from '@mui/material/styles'
import { ChartView, CustomTrace, CUSTOM_UNITS, RechartData, TraceSuffix } from '../../shared/utils/chartModel'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, useMediaQuery } from '@mui/material'
import { UnitType } from 'src/Libs/types'
import { makeStyles } from 'tss-react/mui'
import { AxisInterval } from 'recharts/types/util/types'
import { ZoomIndexType } from 'src/Libs/system-charts/shared'
import { REFERENCE_AREA_STROKE_OPACITY } from '../../shared/utils/consts'
import type { ChartEventHandlers } from '../../shared/chart-event-handlers'

const useStyles = makeStyles()(theme => ({
  brush: {
    '.recharts-brush-texts': {
      display: 'none',
    },
  },
}))
interface BarChartProps {
  unit: UnitType
  secondUnit?: string
  minPrefix?: number
  xAxisTicks: number[]
  xAxisFormat: string
  xAxisInterval?: AxisInterval
  rechartData: RechartData
  customTraces?: CustomTrace[]
  showForecast: boolean
  impossibleTraces: string[]
  hiddenTraces: string[]
  dstSwitchTs?: number
  onElementClick?: (props: Record<string, unknown>) => void
  height?: number
  legendHeight?: number
  headerHeight?: number
  legendWidth?: number
  xAxisDataKey?: string
  traceSuffixes?: TraceSuffix[]
  view?: ChartView
  hasBrush?: boolean
  chartEventHandlers: ChartEventHandlers
  children?: React.ReactNode
  zoomStartIndex?: ZoomIndexType
  zoomEndIndex?: ZoomIndexType
  handleDoubleClick?: () => void
  calculatedMinPrefix?: number
}

type StartEndIndex = {
  startIndex: number
  endIndex: number
}

const BarChart: React.FC<BarChartProps> = ({
  unit,
  secondUnit,
  minPrefix,
  xAxisTicks,
  xAxisFormat,
  xAxisInterval,
  rechartData,
  customTraces,
  showForecast,
  impossibleTraces,
  hiddenTraces,
  dstSwitchTs,
  onElementClick,
  height,
  legendHeight,
  headerHeight,
  legendWidth,
  xAxisDataKey,
  traceSuffixes,
  view,
  hasBrush = true,
  chartEventHandlers,
  children,
  zoomStartIndex,
  zoomEndIndex,
  handleDoubleClick,
  calculatedMinPrefix,
}) => {
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const [startIndex, setStartIndex] = useState<number>()
  const [endIndex, setEndIndex] = useState<number>()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const { classes } = useStyles()

  const bars = useMemo(() => {
    const bars: React.ReactElement[] = []

    const renderHistoryBar = (key: string | null, customTrace: CustomTrace, yAxisId: string, suffix?: TraceSuffix, index?: number): void => {
      if (!key) return

      const completeDataKey = suffix ? `${key}-${suffix.name}` : key
      const suffixColor = suffix && index !== undefined ? customTrace.suffixColors?.[index] : undefined

      bars.push(
        <Bar
          key={completeDataKey}
          dataKey={completeDataKey}
          yAxisId={yAxisId}
          fill={suffixColor?.color ?? customTrace.color ?? getChartColor(customTrace.key)}
          isAnimationActive={false}
          hide={
            impossibleTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
            hiddenTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
            customTrace.hidden
          }
          unit={traceConf[customTrace.key]?.unit ?? unit}
          stackId={customTrace.stackId}
          onClick={onElementClick}
          opacity={downSm ? 0.7 : 1}
        />
      )
    }

    if (!rechartData || !rechartData.data || !customTraces) return bars

    for (const customTrace of customTraces) {
      const yAxisId = CUSTOM_UNITS.includes(traceConf[customTrace.key]?.unit ?? '') ? 'right' : 'left'

      const key = getUniqueIdentifierFromCustomTrace(customTrace)

      if (traceSuffixes?.length) {
        traceSuffixes.forEach((suffix, index) => renderHistoryBar(key, customTrace, yAxisId, suffix, index))
      } else {
        renderHistoryBar(key, customTrace, yAxisId)
      }

      if (traceConf[customTrace.key]?.hasForecast) {
        bars.push(
          <Bar
            key={'f-' + key}
            dataKey={'f-' + key}
            yAxisId={yAxisId}
            fill={customTrace.color ?? getChartColor(customTrace.key)}
            isAnimationActive={false}
            hide={
              impossibleTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
              hiddenTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
              !showForecast ||
              customTrace.hidden
            }
            unit={traceConf[customTrace.key]?.unit ?? unit}
            stackId={customTrace.stackId}
            opacity={0.5}
          />
        )
      }
      // break
    }

    return bars
  }, [rechartData, customTraces, impossibleTraces, hiddenTraces, showForecast]) //eslint-disable-line

  const _CartesianGrid = useMemo(() => CartesianGrid({ isSmall: downSm }), [downSm])
  const _XAxis = useMemo(
    () =>
      XAxis({
        tickFormat: xAxisFormat,
        tick: { fill: theme.palette.text.primary },
        ticks: xAxisTicks,
        xAxisInterval,
        dataKey: xAxisDataKey ?? 'ts',
        mirror: false,
        isSmall: downSm,
      }),
    [xAxisFormat, theme.palette.text.primary, xAxisTicks, xAxisInterval, xAxisDataKey, downSm]
  )
  const _YAxisLeft = useMemo(
    () =>
      YAxisLeft({
        view: view,
        minPrefix: minPrefix,
        tick: { fill: theme.palette.text.primary },
        mirror: true,
        isSmall: downSm,
        exactPrefix: calculatedMinPrefix,
      }),
    [view, theme.palette.text.primary, minPrefix, downSm, calculatedMinPrefix]
  )
  const _YAxisRight = useMemo(
    () =>
      YAxisRight({
        tick: { fill: theme.palette.text.primary },
        unit: secondUnit,
        mirror: downSm,
        isSmall: downSm,
      }),
    [theme.palette.text.primary, secondUnit, downSm]
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBrushChange: any = useCallback((event: StartEndIndex) => {
    setStartIndex(event.startIndex)
    setEndIndex(event.endIndex)
  }, [])

  return (
    <Box sx={{ userSelect: 'none', width: '100%' }} onDoubleClick={handleDoubleClick}>
      <ResponsiveContainer minHeight={downMd ? 350 : undefined} height={height} width={'100%'}>
        <RCBarChart
          barGap={0}
          barCategoryGap={'5%'}
          stackOffset={'sign'}
          data={rechartData.data}
          style={
            upMd
              ? undefined
              : {
                  userSelect: 'none',
                }
          }
          {...chartEventHandlers}
        >
          {zoomStartIndex && zoomEndIndex && (
            <ReferenceArea yAxisId={'right'} x1={zoomStartIndex} x2={zoomEndIndex} strokeOpacity={REFERENCE_AREA_STROKE_OPACITY} />
          )}
          {_CartesianGrid}
          {_XAxis}
          {children}
          {downSm && hasBrush && (
            <Brush height={25} dataKey={'%'} startIndex={startIndex} endIndex={endIndex} onChange={handleBrushChange} className={classes.brush} />
          )}

          {dstSwitchTs && (
            <ReferenceLine
              x={dstSwitchTs}
              xAxisId={0}
              yAxisId={'left'}
              label={t('systemDataHandler.dstSwitch') as string}
              stroke="red"
              strokeDasharray={'3 3'}
              alwaysShow
              isFront
            />
          )}
          {bars}
          {_YAxisLeft}
          {_YAxisRight}
        </RCBarChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default BarChart
